import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Clock3, Users, Video } from "lucide-react";
import { getErrorMessage } from "@/lib/errorMessages";
import { handleRateLimitError } from "@/lib/rateLimitHandler";
import { isValidMeetingCode } from "@/lib/meetingUtils";

type MeetingLookup = {
  id: string;
  title: string | null;
  meeting_code: string;
  waiting_room_enabled: boolean;
  is_active: boolean;
  has_password: boolean;
  scheduled_start: string | null;
};

const fetchMeetingByCode = async (normalizedCode: string) => {
  // Prefer the RPC for business logic (password, scheduling awareness)
  const { data, error } = await supabase
    .rpc('get_meeting_by_code', { _meeting_code: normalizedCode })
    .maybeSingle();

  if (data) {
    return { data: data as MeetingLookup, error: null };
  }

  // Fallback to a direct table query if the RPC isn't available in the current
  // environment so users can still join with their link instead of hitting an
  // opaque error.
  const { data: tableData, error: tableError } = await supabase
    .from('meetings')
    .select('id, title, meeting_code, waiting_room_enabled, is_active, password, scheduled_start')
    .eq('meeting_code', normalizedCode)
    .maybeSingle();

  if (tableData) {
    const meeting: MeetingLookup = {
      id: tableData.id,
      title: tableData.title,
      meeting_code: tableData.meeting_code,
      waiting_room_enabled: tableData.waiting_room_enabled,
      is_active: tableData.is_active,
      has_password: Boolean((tableData as any).password),
      scheduled_start: tableData.scheduled_start,
    };

    return { data: meeting, error: null };
  }

  return { data: null, error: error || tableError };
};

export default function JoinMeeting() {
  const { meetingCode: codeFromUrl } = useParams<{ meetingCode?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [meetingCode, setMeetingCode] = useState(codeFromUrl || searchParams.get("code") || "");
  const [password, setPassword] = useState("");
  const [guestName, setGuestName] = useState("");
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [waitingStatus, setWaitingStatus] = useState<{ meetingId: string; meetingTitle?: string; meetingCode?: string } | null>(null);
  const [waitingMessage, setWaitingMessage] = useState<string | null>(null);
  const [waitingTitle, setWaitingTitle] = useState<string>("Waiting Room");
  const [admissionChannel, setAdmissionChannel] = useState<ReturnType<typeof supabase.channel> | null>(null);
  const [waitingError, setWaitingError] = useState<string | null>(null);
  const [pollIntervalId, setPollIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [activationPollId, setActivationPollId] = useState<NodeJS.Timeout | null>(null);
  useEffect(() => {
    checkUser();
    restoreAvSettings();

    return () => {
      stopPreview();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
      if (activationPollId) {
        clearInterval(activationPollId);
      }
      if (admissionChannel) {
        supabase.removeChannel(admissionChannel);
      }
    };
  }, [pollIntervalId, activationPollId, admissionChannel]);

  const getJoinErrorDetails = (error: any) => {
    const status = error?.status ?? error?.cause?.status;
    const code = error?.code;
    const message = (error?.message || "").toLowerCase();

    if (status === 401 || status === 403) {
      return {
        title: "Sign in required",
        description: "Your session expired. Please sign in again to join this meeting.",
      };
    }

    if (status === 404 || message.includes("not found")) {
      return {
        title: "Meeting not found",
        description: "We couldn't find that meeting. Double-check the code and try again.",
      };
    }

    if (status === 422 || code === "PGRST116") {
      return {
        title: "Invalid meeting request",
        description: "The meeting details look incorrect. Please re-enter the code and password.",
      };
    }

    if (message.includes("permission")) {
      return {
        title: "Permission required",
        description: "Allow camera/mic access or join with them off to continue.",
      };
    }

    return getErrorMessage(error);
  };

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user || null);
  };

  const persistAvSettings = (settings: Record<string, any>) => {
    const existing = JSON.parse(localStorage.getItem("joinAvSettings") || "{}");
    localStorage.setItem("joinAvSettings", JSON.stringify({ ...existing, ...settings }));
  };

  const restoreAvSettings = () => {
    const stored = localStorage.getItem("joinAvSettings");
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed.videoOn === "boolean") setIsVideoOn(parsed.videoOn);
      if (typeof parsed.audioMuted === "boolean") setIsMuted(parsed.audioMuted);
    } catch (error) {
      console.warn("Unable to parse AV settings", error);
    }
  };

  const stopPreview = () => {
    previewStream?.getTracks().forEach((track) => track.stop());
    setPreviewStream(null);
  };

  const startPreview = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) return;
      stopPreview();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: isVideoOn,
        audio: true,
      });
      setPreviewStream(stream);

      stream.getVideoTracks().forEach((track) => {
        track.enabled = isVideoOn;
      });
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !isMuted;
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        const playPromise = videoRef.current.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((error: unknown) => {
            const err = error as { name?: string };

            if (err?.name === "AbortError") return;

            console.error("Preview play failed", error);
            setInlineErrors((prev) => ({ ...prev, preview: "Unable to start video preview." }));
          });
        }
      }
    } catch (error) {
      console.error("Preview start failed", error);
      setInlineErrors((prev) => ({ ...prev, preview: "Unable to access camera or microphone." }));
    }
  };

  useEffect(() => {
    startPreview();
    persistAvSettings({
      videoOn: isVideoOn,
      audioMuted: isMuted,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoOn, isMuted]);

  useEffect(() => {
    if (videoRef.current && previewStream) {
      videoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  const getOrCreateGuestId = () => {
    const storedGuest = localStorage.getItem("guestUserId");
    if (storedGuest) return storedGuest;
    const newGuestId = crypto.randomUUID();
    localStorage.setItem("guestUserId", newGuestId);
    return newGuestId;
  };

  const subscribeToAdmission = (meetingId: string, userId: string, nextPath: string) => {
    const channel = supabase
      .channel(`waiting-room-${meetingId}-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "waiting_room_queue",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const status = (payload.new as any).status;
          if (status === "admitted") {
            navigate(nextPath);
          }
          if (status === "denied") {
            setWaitingError("Host denied your request. You can try again later.");
          }
        }
      )
      .subscribe();

    setAdmissionChannel(channel);
  };

  const pollAdmission = (meetingId: string, userId: string, nextPath: string) => {
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from("waiting_room_queue")
        .select("status")
        .eq("meeting_id", meetingId)
        .eq("user_id", userId)
        .maybeSingle();

      if (data?.status === "admitted") {
        clearInterval(interval);
        navigate(nextPath);
      }
      if (data?.status === "denied") {
        clearInterval(interval);
        setWaitingError("Host denied your request. You can try again later.");
      }
    }, 4000);
    setPollIntervalId(interval);
  };

  const clearActivationPolling = () => {
    if (activationPollId) {
      clearInterval(activationPollId);
      setActivationPollId(null);
    }
  };

  const proceedToMeeting = async (meetingDetails: MeetingLookup) => {
    clearActivationPolling();

    const nextPath = !user && guestName.trim()
      ? `/meeting/${meetingDetails.id}?guestName=${encodeURIComponent(guestName.trim())}`
      : `/meeting/${meetingDetails.id}`;

    setInlineErrors({});
    setLoading(false);
    persistAvSettings({ videoOn: isVideoOn, audioMuted: isMuted });

    if (meetingDetails.waiting_room_enabled) {
      setWaitingTitle("Waiting Room");
      setIsWaiting(true);
      setWaitingStatus({ meetingId: meetingDetails.id, meetingTitle: meetingDetails.title || "Meeting", meetingCode: meetingDetails.meeting_code });
      setWaitingMessage("Waiting for the host to admit you...");

      const userId = user?.id || getOrCreateGuestId();

      const { error: queueError } = await supabase
        .from("waiting_room_queue")
        .insert({
          meeting_id: meetingDetails.id,
          user_id: userId,
          guest_name: !user ? guestName.trim() : null,
          status: "waiting",
        });

      if (queueError) {
        throw queueError;
      }

      subscribeToAdmission(meetingDetails.id, userId, nextPath);
      pollAdmission(meetingDetails.id, userId, nextPath);
    } else {
      setIsWaiting(false);
      navigate(nextPath);
    }
  };

  const startActivationPolling = (meetingDetails: MeetingLookup, normalizedCode: string) => {
    if (activationPollId) {
      clearInterval(activationPollId);
    }

    const interval = setInterval(async () => {
      const { data, error } = await fetchMeetingByCode(normalizedCode);

      if (error) {
        console.error("Error polling meeting activation:", error);
        return;
      }

      if (data?.is_active) {
        clearInterval(interval);
        setActivationPollId(null);
        setWaitingMessage("The host just started the meeting. Joining now...");
        try {
          await proceedToMeeting({
            ...meetingDetails,
            ...data,
            title: data.title ?? meetingDetails.title,
          });
        } catch (joinError) {
          console.error("Error joining after activation:", joinError);
          toast({
            title: "Unable to join",
            description: "We couldn't join after the host started. Please try again.",
            variant: "destructive",
          });
          setLoading(false);
        }
      }
    }, 4000);

    setActivationPollId(interval);
  };

  const handleJoinMeeting = async () => {
    const errors: Record<string, string> = {};

    if (!meetingCode.trim()) {
      errors.meetingCode = "Please enter a meeting code.";
    } else if (!isValidMeetingCode(meetingCode)) {
      errors.meetingCode = "Invalid meeting code format. Please check and try again.";
    }

    if (!user && !guestName.trim()) {
      errors.guestName = "Please enter your name to join as a guest.";
    }

    setInlineErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      const normalizedCode = meetingCode.trim().toUpperCase();

      const { data: meetingInfo, error: meetingLookupError } = await fetchMeetingByCode(normalizedCode);

      if (meetingLookupError) {
        console.error("Meeting lookup error:", meetingLookupError);
        setInlineErrors({ meetingCode: "Failed to look up this meeting. Please try again." });
        setLoading(false);
        return;
      }

      if (!meetingInfo) {
        setInlineErrors({ meetingCode: "Meeting not found. Please check the code." });
        setLoading(false);
        return;
      }

      if (meetingInfo.has_password && !password) {
        setInlineErrors({ password: "This meeting requires a password." });
        setLoading(false);
        return;
      }

      if (meetingInfo.has_password) {
        const { data: verification, error: verificationError } = await supabase
          .rpc('verify_meeting_password', {
            _meeting_code: normalizedCode,
            _password: password,
          })
          .maybeSingle();

        if (verificationError) {
          console.error("Meeting verification error:", verificationError);
          setInlineErrors({ meetingCode: "Failed to verify meeting. Please try again." });
          setLoading(false);
          return;
        }

        if (!verification?.is_valid) {
          setInlineErrors({ password: "Incorrect password. Please try again." });
          setLoading(false);
          return;
        }
      }

      const meetingDetails: MeetingLookup = {
        id: meetingInfo.id,
        title: meetingInfo.title,
        meeting_code: meetingInfo.meeting_code,
        waiting_room_enabled: meetingInfo.waiting_room_enabled,
        is_active: meetingInfo.is_active,
        has_password: meetingInfo.has_password,
        scheduled_start: meetingInfo.scheduled_start,
      };

      if (!meetingDetails.is_active) {
        setInlineErrors({});
        setWaitingTitle("Waiting for host");
        setIsWaiting(true);
        setWaitingStatus({ meetingId: meetingDetails.id, meetingTitle: meetingDetails.title || "Meeting", meetingCode: meetingDetails.meeting_code });
        setWaitingMessage(meetingDetails.scheduled_start
          ? `This meeting is scheduled for ${new Date(meetingDetails.scheduled_start).toLocaleString()}. We'll join once it starts.`
          : "This meeting hasn't started yet. We'll join as soon as the host starts it.");
        setLoading(false);
        startActivationPolling(meetingDetails, normalizedCode);
        return;
      }

      await proceedToMeeting(meetingDetails);
    } catch (error: any) {
      // Check if this is a rate limit error
      const isRateLimit = handleRateLimitError(error, 'meeting_join');

      if (!isRateLimit) {
        const { title, description } = getJoinErrorDetails(error);
        toast({
          title,
          description,
          variant: "destructive",
          action: (
            <ToastAction altText="Retry join" onClick={() => handleJoinMeeting()}>
              Retry
            </ToastAction>
          ),
        });
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-4 lg:p-8">
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-primary opacity-20 blur-3xl rounded-full animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-secondary opacity-20 blur-3xl rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      <div className="w-full max-w-2xl mx-auto relative z-10">
        <Card className="w-full shadow-card backdrop-blur-sm bg-card/95 border-border/50">
          <CardHeader className="text-center space-y-4 sm:space-y-6 pb-4 sm:pb-6 lg:pb-8">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-primary rounded-2xl blur-xl opacity-60 animate-pulse" />
                <div className="relative p-4 lg:p-5 bg-gradient-primary rounded-2xl shadow-glow">
                  <Video className="h-10 w-10 lg:h-12 lg:w-12 text-primary-foreground" />
                </div>
              </div>
            </div>

            <div className="space-y-1 sm:space-y-2">
              <CardTitle className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                Join Meeting
              </CardTitle>
              <CardDescription className="text-sm sm:text-base lg:text-lg text-muted-foreground">
                Enter your meeting code and personalize your entry in seconds
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 lg:space-y-10 px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-[1.05fr,0.95fr]">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="meetingCode" className="text-sm lg:text-base font-medium text-foreground">
                    Meeting Code
                  </Label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Video className="h-5 w-5 lg:h-6 lg:w-6 text-muted-foreground" />
                    </div>
                    <Input
                      id="meetingCode"
                      placeholder="XXXX-XXXX-XXXX"
                      value={meetingCode}
                      onChange={(e) => setMeetingCode(e.target.value.toUpperCase())}
                      disabled={!!codeFromUrl}
                      className="pl-12 lg:pl-14 h-12 lg:h-14 text-base lg:text-lg font-mono tracking-wider bg-muted/50 border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:border-border/70"
                    />
                  </div>
                  {inlineErrors.meetingCode && (
                    <p className="text-sm text-destructive">{inlineErrors.meetingCode}</p>
                  )}
                </div>

                {!user && (
                  <div className="space-y-2">
                    <Label htmlFor="guestName" className="text-sm lg:text-base font-medium text-foreground flex items-center gap-2">
                      Your Name
                      <span className="text-xs lg:text-sm text-destructive font-normal">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <Users className="h-5 w-5 lg:h-6 lg:w-6 text-muted-foreground" />
                      </div>
                      <Input
                        id="guestName"
                        placeholder="Enter your name"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        maxLength={50}
                        className="pl-12 lg:pl-14 h-12 lg:h-14 text-base lg:text-lg bg-muted/50 border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:border-border/70"
                      />
                    </div>
                    {inlineErrors.guestName && (
                      <p className="text-sm text-destructive">{inlineErrors.guestName}</p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm lg:text-base font-medium text-foreground flex items-center gap-2">
                    Password
                    <span className="text-xs lg:text-sm text-muted-foreground font-normal">(Optional)</span>
                  </Label>
                  <PasswordInput
                    id="password"
                    placeholder="Enter password if required"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 lg:h-14 text-base lg:text-lg bg-muted/50 border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:border-border/70"
                  />
                </div>

                <div className="flex flex-wrap gap-2 items-center text-sm text-muted-foreground">
                  <Badge variant="secondary">Fast join</Badge>
                  <span>Use your link, add your name, and hop right in.</span>
                </div>

                <Button
                  onClick={handleJoinMeeting}
                  className="w-full h-12 sm:h-14 lg:h-14 text-sm sm:text-base lg:text-lg font-semibold bg-gradient-primary hover:opacity-90 transition-all shadow-glow hover:shadow-stream hover:scale-[1.01]"
                  disabled={!meetingCode.trim() || loading || (!user && !guestName.trim())}
                >
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 lg:h-5 lg:w-5 mr-2" />
                  {loading ? "Joining..." : user ? "Join Meeting" : "Join as Guest"}
                </Button>
              </div>

              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/40 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-primary" />
                    <p className="text-sm font-medium">Quick preview</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={isMuted ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => setIsMuted((prev) => !prev)}
                    >
                      {isMuted ? "Unmute" : "Mute"}
                    </Button>
                    <Button
                      variant={isVideoOn ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => setIsVideoOn((prev) => !prev)}
                    >
                      {isVideoOn ? "Camera off" : "Camera on"}
                    </Button>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-lg border border-border/60 bg-black/70 relative overflow-hidden">
                  <video ref={videoRef} className="h-full w-full object-contain" muted playsInline />
                  {!isVideoOn && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 text-muted-foreground">
                      <Video className="h-8 w-8" />
                      <p className="text-sm">Camera paused</p>
                    </div>
                  )}
                </div>
                {inlineErrors.preview && <p className="text-sm text-destructive">{inlineErrors.preview}</p>}
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div className="rounded-md border border-border/50 bg-background/60 p-2">
                    <p className="font-semibold text-foreground">Mic</p>
                    <p>{isMuted ? "Muted for entry" : "Open and ready"}</p>
                  </div>
                  <div className="rounded-md border border-border/50 bg-background/60 p-2">
                    <p className="font-semibold text-foreground">Camera</p>
                    <p>{isVideoOn ? "Preview live" : "Off by default"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center pt-2 pb-2">
              <Button
                variant="ghost"
                onClick={() => navigate(user ? "/dashboard" : "/")}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm lg:text-base"
              >
                {user ? "Back to Dashboard" : "Back to Home"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {isWaiting && waitingStatus && (
        <div className="fixed inset-0 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4 z-20">
          <Card className="max-w-lg w-full shadow-glow">
            <CardHeader className="text-center space-y-2">
              <div className="flex justify-center">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock3 className="h-7 w-7 text-primary animate-pulse" />
                </div>
              </div>
              <CardTitle>{waitingTitle}</CardTitle>
              <CardDescription>
                {waitingMessage || "Stay on this screen and we’ll bring you in as soon as the host admits you."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-border/60 bg-muted/40 p-4 space-y-2">
                <p className="text-sm font-semibold">Meeting</p>
                <p className="text-lg font-bold">{waitingStatus.meetingTitle}</p>
                <p className="text-xs text-muted-foreground">Code: {waitingStatus.meetingCode || meetingCode}</p>
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/40 p-4 space-y-2">
                <p className="text-sm font-semibold">Status</p>
                <p className="text-sm text-muted-foreground">{waitingMessage}</p>
              </div>
              {waitingError && <p className="text-sm text-destructive">{waitingError}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => navigate(user ? "/dashboard" : "/")}>Cancel</Button>
                <Button onClick={handleJoinMeeting} disabled={loading}>Retry</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
